import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardHeader from "../components/DashboardHeader";
import Modal from "../components/PlannerPostModal";
import { getToken } from "../lib/session";

const API_BASE = process.env.REACT_APP_API || "http://localhost:4500/api";
const CONTENT_ENDPOINT = `${API_BASE}/dashboard/content`;
const BRANDS_ENDPOINT = `${API_BASE}/brands/user`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tab = params.get("tab") || "drafts"; // drafts | published | brands
  const [previewPost, setPreviewPost] = useState(null);
  const openPreview = (post) => setPreviewPost(post);
  const [brands, setBrands] = useState([]);  // State for storing brands
  const [loadingBrands, setLoadingBrands] = useState(true);  // Loading brands flag
  const [loading, setLoading] = useState(false); // Loading state
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);

  // Fetch brands data from the backend
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const token = getToken(); // Get user token for authorization
        const res = await fetch(`${BRANDS_ENDPOINT}`, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!res.ok) {
          console.error("Failed to fetch brands:", res.status);
          setBrands([]);
          setLoadingBrands(false);
          return;
        }

        const data = await res.json();
        setBrands(data);  // Set the brands fetched from the database
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrands([]);
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  // Handle deleting a brand
  const handleDeleteBrand = async (brandId) => {
    const confirmed = window.confirm("Are you sure you want to delete this brand?");
    if (!confirmed) return;  // If user doesn't confirm, don't proceed

    try {
      const token = getToken(); // Get user token for authorization
      const res = await fetch(`${API_BASE}/brands/${brandId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to delete brand:", res.status);
        return;
      }

      // If the delete was successful, remove the brand from the state immediately
      setBrands((prevBrands) => prevBrands.filter((brand) => brand._id !== brandId));

      // Trigger a page refresh to reflect the change
      window.location.reload(); // Refresh the page to reload the updated brands list
    } catch (error) {
      console.error("Error deleting brand:", error);
    }
  };

  // Handle deleting a draft
  const handleDeleteDraft = async (draftId) => {
    const confirmed = window.confirm("Are you sure you want to delete this draft?");
    if (!confirmed) return;  // If user doesn't confirm, don't proceed

    try {
      const token = getToken(); // Get user token for authorization
      const res = await fetch(`${API_BASE}/dashboard/content/${draftId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to delete draft:", res.status);
        return;
      }

      // If the delete was successful, remove the draft from the state immediately
      setRows((prevRows) => prevRows.filter((draft) => draft._id !== draftId));

      // Trigger a page refresh to reflect the change
      window.location.reload(); // Refresh the page to reload the updated drafts list
    } catch (error) {
      console.error("Error deleting draft:", error);
    }
  };

  // Fetch the data for each tab (drafts, published, brands)
  useEffect(() => {
    async function load() {
      setLoading(true);
      // console.log(`Dashboard: fetching data for tab "${tab}"…`);

      try {
        const token = getToken(); // Get token from session
        let res;
        let data;

        if (tab === "drafts" || tab === "published") {
          const status = tab === "drafts" ? "draft" : "published";
          const url = `${CONTENT_ENDPOINT}?status=${encodeURIComponent(status)}&q=${encodeURIComponent(query)}`; // No need to pass userId here
          // console.log("→ GET", url);

          res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined, // Add token for authorization
          });

          if (!res.ok) {
            const txt = await res.text();
            console.error("Content API failed:", res.status, txt);
            setRows([]);
            return;
          }

          data = await res.json();
          // console.log("Content API payload:", data);
          setRows(Array.isArray(data.content) ? data.content : []);
        } else if (tab === "brands") {
          const url = `${BRANDS_ENDPOINT}?q=${encodeURIComponent(query)}`; // Only fetch brands if applicable
          // console.log("→ GET", url);

          res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined, // Add token for authorization
          });

          if (!res.ok) {
            const txt = await res.text();
            console.error("Brands API failed:", res.status, txt);
            setRows([]);
            return;
          }

          data = await res.json();
          // console.log("Brands API payload:", data);
          setRows(Array.isArray(data) ? data : []);
        } else {
          setRows([]);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [tab, query]);

  const handleBrandClick = (brandId) => {
    // console.log(`Navigating to brand with ID: ${brandId}`);
    navigate(`/brands/${brandId}`);
  };

  const handleDraftClick = (draft) => {
    // console.log("Opening draft:", draft);
    setPreviewPost(draft);  // Ensure you're passing the correct draft here
  };

  return (
    <div className="bg-white min-h-screen text-slate-800">
      <DashboardHeader />

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-8">
          My Content
        </h1>

        {/* Tabs */}
        <div className="flex items-center gap-10 text-lg">
          <TabLink active={tab === "drafts"} label="Drafts" href="/dashboard?tab=drafts" />
          <TabLink active={tab === "published"} label="Published" href="/dashboard?tab=published" />
          <TabLink active={tab === "brands"} label="Brands" href="/dashboard?tab=brands" />
        </div>

        {/* Search */}
        <div className="mt-6">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 h-12">
            <span className="opacity-60">🔍</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, brand name or website"
              className="flex-1 outline-none"
            />
            <button onClick={() => setQuery("")} className="opacity-60">↻</button>
          </div>
        </div>

        {/* Content */}
        <section className="mt-10">
          {loading ? (
            <div className="text-center text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState tab={tab} />
          ) : tab === "brands" ? (
            <BrandGrid items={rows} onBrandClick={handleBrandClick} onDeleteClick={handleDeleteBrand} />
          ) : (
            <VideoGrid items={rows} onDraftClick={handleDraftClick} onDeleteClick={handleDeleteDraft} />
          )}
        </section>
      </main>

      {/* Preview Modal */}
      {previewPost && (
        <Modal open={!!previewPost} post={previewPost} onClose={() => setPreviewPost(null)} />
      )}
    </div>
  );
}

function TabLink({ active, label, href }) {
  return (
    <a href={href} className="relative pb-2">
      <span className="hover:text-slate-900">{label}</span>
      {active && (
        <span className="absolute -bottom-[6px] left-0 right-0 h-[2px] bg-slate-800 rounded-full" />
      )}
    </a>
  );
}

function EmptyState({ tab }) {
  const txt =
    tab === "brands"
      ? "No brands yet"
      : tab === "published"
      ? "No published content yet"
      : "No drafts yet";
  return (
    <div className="py-16 grid place-items-center text-slate-500">
      <div className="w-48 h-48 rounded-full bg-slate-100 grid place-items-center mb-4">
        📦
      </div>
      <div>{txt}. Use “Create New” to get started.</div>
    </div>
  );
}

function VideoGrid({ items, onDraftClick, onDeleteClick }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((v) => (
        <article
          key={v._id}
          className="border border-slate-200 rounded-xl p-4 cursor-pointer flex flex-col bg-white shadow-md hover:shadow-lg transition-transform transform hover:scale-105"
        >
          {/* Draft Image */}
          <div className="relative mb-4 h-48 bg-slate-100 rounded-lg overflow-hidden">
            {v.thumbUrl ? (
              <img
                src={v.thumbUrl}
                alt={v.title || "Draft Image"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                No Image
              </div>
            )}
          </div>

          {/* Draft Details */}
          <div className="flex-1">
            <div className="text-sm text-slate-500 mb-2">
              {v.status === "published" ? "Published" : "Draft"} ·{" "}
              {new Date(v.updatedAt || v.createdAt).toLocaleString()}
            </div>
            <h3 className="font-semibold text-lg text-slate-800">
              {v.title || "Untitled"}
            </h3>
            {v.brand?.name && (
              <div className="text-sm text-slate-500 mt-1">{v.brand.name}</div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex justify-between">
            <button
              onClick={() => onDraftClick(v)} // Preview the draft
              className="text-blue-500 hover:text-blue-700"
            >
              Preview
            </button>
            <button
              onClick={() => onDeleteClick(v._id)} // Delete the draft
              className="text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}


function BrandGrid({ items, onBrandClick, onDeleteClick }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((b) => (
        <article
          key={b._id}
          className="border border-slate-200 rounded-xl p-4 cursor-pointer"
        >
          <h3 className="font-semibold">{b.name || b.website || "Brand"}</h3>
          <div className="text-sm text-slate-500 mt-1">{b.website}</div>

          <div className="mt-4 flex justify-between">
            <button
              onClick={() => onBrandClick(b._id)} // View brand details
              className="text-blue-500 hover:text-blue-700"
            >
              View
            </button>
            <button
              onClick={() => onDeleteClick(b._id)} // Delete the brand
              className="text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
